import { useState } from 'react';
// @mui
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
// components
import Iconify from 'src/components/iconify';
//
import { useOnboarding } from './onboarding-context';
import { GlossaryDialog, ShortcutsDialog, WhatsNewDialog, ChecklistDialog, AllToursDialog } from './onboarding-dialogs';

// The §7 Help menu: "tours for this page / all tours / checklist / shortcuts
// / what's new / glossary / WhatsApp support link / reset".
//
// No company/support WhatsApp number is configured anywhere in this codebase
// (checked server/.env and the frontend for an existing pattern — there is
// none). This is a placeholder number, clearly flagged as such rather than
// silently shipped as real.
const SUPPORT_WHATSAPP_NUMBER = '910000000000'; // PLACEHOLDER — replace with Yurme's real support number.

export default function HelpPopover() {
  const { state, tours, startTour, resetOnboarding } = useOnboarding();
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialog, setDialog] = useState(null); // 'glossary' | 'shortcuts' | 'whats-new' | 'checklist' | 'all-tours' | null

  const checklistPending = state ? state.checklistTotal - state.checklistDone : 0;
  const unseenWhatsNew = state?.unseenWhatsNewCount || 0;

  function toursForThisPage() {
    return tours.filter((tour) => tour.steps.some((step) => document.querySelector(`[data-tour="${step.selector}"]`)));
  }

  function handleTourForThisPage() {
    const candidates = toursForThisPage();
    if (candidates[0]) startTour(candidates[0].key);
    setAnchorEl(null);
  }

  function handleReset() {
    resetOnboarding();
    setAnchorEl(null);
  }

  return (
    <>
      <Tooltip title="Help">
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <Badge badgeContent={checklistPending + unseenWhatsNew} color="error" max={9} invisible={!checklistPending && !unseenWhatsNew}>
            <Iconify icon="solar:question-circle-bold-duotone" width={24} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)} sx={{ width: 260 }}>
        <MenuItem onClick={handleTourForThisPage} disabled={!toursForThisPage().length}>
          <ListItemIcon><Iconify icon="solar:map-point-bold-duotone" width={20} /></ListItemIcon>
          <ListItemText>Tour this page</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setDialog('all-tours'); setAnchorEl(null); }}>
          <ListItemIcon><Iconify icon="solar:route-bold-duotone" width={20} /></ListItemIcon>
          <ListItemText>All tours</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setDialog('checklist'); setAnchorEl(null); }}>
          <ListItemIcon><Iconify icon="solar:checklist-minimalistic-bold-duotone" width={20} /></ListItemIcon>
          <ListItemText>Getting-started checklist{checklistPending ? ` (${checklistPending})` : ''}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setDialog('shortcuts'); setAnchorEl(null); }}>
          <ListItemIcon><Iconify icon="solar:keyboard-bold-duotone" width={20} /></ListItemIcon>
          <ListItemText>Keyboard shortcuts</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setDialog('whats-new'); setAnchorEl(null); }}>
          <ListItemIcon><Iconify icon="solar:gift-bold-duotone" width={20} /></ListItemIcon>
          <ListItemText>What&apos;s new{unseenWhatsNew ? ` (${unseenWhatsNew})` : ''}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setDialog('glossary'); setAnchorEl(null); }}>
          <ListItemIcon><Iconify icon="solar:book-bookmark-bold-duotone" width={20} /></ListItemIcon>
          <ListItemText>Glossary</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          component="a"
          href={`https://wa.me/${SUPPORT_WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener"
          onClick={() => setAnchorEl(null)}
        >
          <ListItemIcon><Iconify icon="ic:baseline-whatsapp" width={20} /></ListItemIcon>
          <ListItemText>WhatsApp support</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleReset}>
          <ListItemIcon><Iconify icon="solar:restart-bold-duotone" width={20} /></ListItemIcon>
          <ListItemText>Reset onboarding</ListItemText>
        </MenuItem>
      </Menu>

      <GlossaryDialog open={dialog === 'glossary'} onClose={() => setDialog(null)} />
      <ShortcutsDialog open={dialog === 'shortcuts'} onClose={() => setDialog(null)} />
      <WhatsNewDialog open={dialog === 'whats-new'} onClose={() => setDialog(null)} />
      <ChecklistDialog open={dialog === 'checklist'} onClose={() => setDialog(null)} />
      <AllToursDialog open={dialog === 'all-tours'} onClose={() => setDialog(null)} />
    </>
  );
}
